import FormField from '@ingka/form-field';
import QuantityStepper from '@ingka/quantity-stepper';
import Text from '@ingka/text';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shouldValidate } from '../../utils';
import { FormFieldChildren } from '../controlled-year-month-dropdown/FormFieldChildren';
import styles from './ControlledQuantityStepper.module.scss';

export type ControlledQuantityStepperProps<TFormData extends FieldValues = FieldValues> = {
  control: Control<TFormData>;
  name: Path<TFormData>;
  label: string;
  minVal: number;
  maxVal: number;
  disabled?: boolean;
};

export const ControlledQuantityStepper = <TFormData extends FieldValues = FieldValues>({
  control,
  name,
  label,
  disabled = false,
  minVal,
  maxVal,
}: ControlledQuantityStepperProps<TFormData>) => {
  const { t } = useTranslation();

  const clamp = (value: number) => Math.min(Math.max(value, minVal), maxVal);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
        // After every onChange we also call onBlur. This is to make sure that
        // validation gets triggered since there are many ways to update this
        // input, not just by inputting into the <input>-element itself.
        if (value < minVal) {
          onChange(minVal);
          onBlur();
        }
        const increase = () => {
          onChange(value !== undefined ? clamp(value + 1) : minVal);
          onBlur();
        };
        const decrease = () => {
          onChange(value !== undefined ? clamp(value - 1) : minVal);
          onBlur();
        };
        // Have to reimplement arrow up/down when using it as a controlled
        // component. Otherwise this component support this out the box.
        const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
          switch (e.key) {
            case 'ArrowUp': {
              e.preventDefault();
              increase();
              break;
            }
            case 'ArrowDown': {
              e.preventDefault();
              decrease();
              break;
            }
          }
        };
        return (
          <FormField
            shouldValidate={shouldValidate(error)}
            valid={!error}
            validation={{
              msg: error?.message ? `${t(error.message)}` : '',
              type: 'error',
            }}
            data-testid={`quantity-stepper-${name.replace(/\./g, '-')}`}
          >
            <FormFieldChildren
              render={(describedById) => (
                <>
                  <Text className={styles['label']}>{label}</Text>
                  <QuantityStepper
                    name={name}
                    small
                    minVal={minVal}
                    maxVal={maxVal}
                    value={value}
                    ariaDescribedById={describedById ?? `${name}-aria-described-by-id`}
                    // From Skapa docs: "Make sure to announce the minimum value in your
                    // description. Example: ‘Type in a number or use arrow up or arrow down to
                    // change the quantity. The minimum quantity is 1’. This is especially
                    // important if the minimum value is bigger than 0."
                    // https://react.skapa.ikea.net/?path=/docs/inputs-controls-quantity-stepper--default
                    ariaDescribedBy={`${t('aria-quantity-stepper-description', {
                      minimumQuantity: minVal,
                    })}`}
                    onIncrease={increase}
                    onDecrease={decrease}
                    onInputChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(
                        event.currentTarget.value !== ''
                          ? clamp(parseInt(event.currentTarget.value, 10))
                          : minVal
                      );
                      onBlur();
                    }}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                    disabled={disabled}
                    autoComplete="off"
                    data-testid={`quantity-stepper-input-${name.replace(/\./g, '-')}`}
                  />
                </>
              )}
            />
          </FormField>
        );
      }}
    />
  );
};
